import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunkWithTraceID } from "@lib/redux";
import { fetchCollectionList, fetchProductList as newFetchProductList } from "@apis/http/api";
import { UserAiJobItemResponseStatus } from "@apis/generated/openapi";

const VIEWED_CARDS_KEY = "viewed_video_generate_cards";

// 获取已查看的卡片 ID 列表
export const getViewedCards = (): string[] => {
  try {
    const stored = localStorage.getItem(VIEWED_CARDS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// 标记卡片为已查看
export const markCardAsViewed = (id: string | number) => {
  try {
    const viewed = getViewedCards();
    if (!viewed.includes(String(id))) {
      viewed.push(String(id));
      localStorage.setItem(VIEWED_CARDS_KEY, JSON.stringify(viewed));
    }
  } catch (error) {
    console.error("Failed to save viewed card:", error);
  }
};

// 检查卡片是否已查看
export const isCardViewed = (id: string | number): boolean => {
  return getViewedCards().includes(String(id));
};

export const statusConfig: Record<UserAiJobItemResponseStatus, { label: string; color: string }> = {
  processing: { label: "Processing", color: "processing" },
  queued: { label: "Queued", color: "default" },
  completed: { label: "Completed", color: "success" },
  failed: { label: "Failed", color: "error" },
};

export const FocusTypeEnum = {
  Video: "video",
  Image: "image",
  Script: "script",
  Product: "product",
  Character: "character",
} as const;
export type FocusType = (typeof FocusTypeEnum)[keyof typeof FocusTypeEnum];

export const VideoTypeEnum = {
  Generated: "generated",
  Uploaded: "uploaded",
  Saved: "saved",
} as const;
export type VideoType = (typeof VideoTypeEnum)[keyof typeof VideoTypeEnum];

export const ProductTypeEnum = {
  Created: "created",
  Saved: "saved",
} as const;
export type ProductType = (typeof ProductTypeEnum)[keyof typeof ProductTypeEnum];

export const CharacterTypeEnum = {
  Model: "model",
  Animal: "animal",
} as const;
export type CharacterType = (typeof CharacterTypeEnum)[keyof typeof CharacterTypeEnum];

export interface LibraryState {
  lastViewedTimestamp: number; // 用于触发 selector 重新计算

  // focusList 相关状态
  focusType: FocusType;
  videoType: VideoType;
  productType: ProductType;
  characterType: CharacterType;

  // 回收站弹窗状态
  trashDisplay: boolean;
}

// 商品列表
export const fetchProductList = createAsyncThunk("library/fetchProductList", async () => {
  return await newFetchProductList();
});

// 脚本、视频收藏列表
export const fetchCollectionListThunk = createAsyncThunkWithTraceID<any[], { type: string | number; data?: unknown }>(
  "library/fetchCollectionList",
  async ({ type }) => {
    return (await fetchCollectionList({ type } as any)) as unknown as any[];
  },
);

const initialState: LibraryState = {
  // focusList 相关初始状态
  focusType: "video",
  videoType: "generated",
  productType: "created",
  characterType: "model",

  lastViewedTimestamp: 0,
  // 回收站弹窗状态
  trashDisplay: false,
};

const librarySlice = createSlice({
  name: "library",
  initialState,
  reducers: {
    // 更新已查看时间戳，触发 selector 重新计算
    updateViewedTimestamp: (state) => {
      state.lastViewedTimestamp = Date.now();
    },
    updateFocusType: (state, action: PayloadAction<FocusType>) => {
      state.focusType = action.payload;
    },
    updateVideoType: (state, action: PayloadAction<VideoType>) => {
      state.videoType = action.payload;
    },
    updateProductType: (state, action: PayloadAction<ProductType>) => {
      state.productType = action.payload;
    },
    updateCharacterType: (state, action: PayloadAction<CharacterType>) => {
      state.characterType = action.payload;
    },
    showTrashDisplay: (state) => {
      state.trashDisplay = true;
    },
    hideTrashDisplay: (state) => {
      state.trashDisplay = false;
    },
  },
});

export const {
  updateViewedTimestamp,
  updateFocusType,
  updateVideoType,
  updateProductType,
  updateCharacterType,
  showTrashDisplay,
  hideTrashDisplay,
} = librarySlice.actions;

export default librarySlice.reducer;
